import React from "react";
import LegalLayout from "./LegalLayout";
import { LINKS } from "../../lib/constants";

export default function TermsOfUse() {
  return (
    <LegalLayout title="Terms of Use" lastUpdated="July 2026">
      <h2>1. Introduction</h2>
      <p>
        These terms govern your use of the ProfitMIND website at <strong>profitmind.co.uk</strong>,
        the related landing pages (including session.profitmind.co.uk), the free Profit &amp; Cashflow
        Clarity Check, and the forecastly cashflow forecasting software ("the Services"). All
        Services are operated by ProfitMIND Ltd (registered in England &amp; Wales). By using any of
        them, you agree to these terms. If you do not agree, please do not use the Services.
      </p>

      <h2>2. Who we are</h2>
      <p>
        Legal entity: ProfitMIND Ltd<br />
        Trading as: forecastly<br />
        Postal address: 4 Capricorn Centre, Cranes Farm Road, Basildon, Essex, SS14 3JJ, United Kingdom<br />
        Email: <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a><br />
        Registered in England and Wales. Reg. no. 09918255
      </p>

      <h2>3. Eligibility</h2>
      <p>
        You must be at least 18 years old and have authority to enter into this agreement on behalf
        of yourself or your business.
      </p>

      <h2>4. The Services</h2>
      <h3>4.1 ProfitMIND advisory services</h3>
      <p>
        The Money Flow Strategy Session and Financial Leadership Advisory Programme are 1:1
        professional services provided by Magdalena Mahdy. These services include financial
        strategy, business mentorship, and dashboards / templates. They <strong>do not</strong>{" "}
        constitute regulated financial advice within the meaning of the Financial Services and
        Markets Act 2000, nor do they constitute legal, tax or accounting advice that replaces your
        own accountant or solicitor. You should always seek qualified professional advice before
        making material business decisions. Specific terms applying to paid services (cancellation,
        refunds, deliverables, IP) are set out in the <a href="/terms-and-conditions">Terms &amp; Conditions of Sale</a>.
      </p>

      <h3>4.2 Free Profit &amp; Cashflow Clarity Check</h3>
      <p>
        The Profit &amp; Cashflow Clarity Check is a free self-assessment tool. It is provided for general
        informational purposes only and does not constitute professional advice. We will use the
        email you provide to send you your personalised report (and, only if you opt in, occasional
        related content).
      </p>

      <h3>4.3 forecastly (software)</h3>
      <p>
        forecastly helps small business owners and solopreneurs build a 12-month cashflow
        forecast, log monthly actuals against that forecast, and receive AI-generated narrative
        insights. The Service is provided "as is" — it is a planning aid, not a substitute for
        professional accounting, tax or legal advice. The forecast you build is based on the
        numbers YOU enter; the accuracy of your forecast depends on the accuracy of your inputs.
      </p>

      <h2>5. Account &amp; security (forecastly)</h2>
      <p>
        You are responsible for keeping your forecastly password secure. Do not share your
        account with others. If you believe your account has been compromised, email{" "}
        <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a> immediately and we will help you
        secure it.
      </p>

      <h2>6. Pricing &amp; payment</h2>
      <h3>6.1 ProfitMIND advisory services</h3>
      <p>
        Prices for the Money Flow Strategy Session and Financial Leadership Advisory Programme are
        shown on the relevant booking page at the time of purchase. Payment is taken in full at the
        point of booking, or in instalments where a payment plan is offered. See our{" "}
        <a href="/terms-and-conditions">Terms &amp; Conditions of Sale</a> for cancellation,
        rescheduling and refund terms.
      </p>

      <h3>6.2 forecastly</h3>
      <p>The Service is offered in two pricing tiers:</p>
      <ul>
        <li><strong>One-off</strong> — a single payment for lifetime access to the core forecasting feature.</li>
        <li><strong>Subscription</strong> — a setup fee plus a recurring monthly or annual fee for ongoing access to actuals tracking, AI insights, and product updates.</li>
      </ul>
      <p>
        Current prices are shown on the pricing page at the time of purchase. Payments are
        processed by Stripe. By starting a subscription you authorise Stripe to charge your saved
        payment method on each renewal date.
      </p>
      <p>
        You can cancel your subscription at any time from inside the app or via the Stripe customer
        portal. Cancellation takes effect at the end of your current billing period — we do not
        pro-rate or refund partial months unless required by consumer law.
      </p>

      <h2>7. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Resell, sublicense or share access to the Services with anyone outside your business.</li>
        <li>Attempt to gain unauthorised access to other users' data or to our infrastructure.</li>
        <li>Use the Services to send spam, malware or anything illegal.</li>
        <li>Use automated tools to scrape, copy or reverse-engineer the Services beyond what is permitted under UK law.</li>
        <li>Submit fake information through forms, assessments or booking systems.</li>
      </ul>
      <p>We may suspend or terminate your account, without refund, if you breach these terms.</p>

      <h2>8. AI-generated insights (forecastly)</h2>
      <p>
        The "Forecast Story", goal action plans, variance insights and Big Decision framing inside
        forecastly are generated by a large language model. We curate the prompts and inject
        only your numbers + your goals, but the model output is non-deterministic and may contain
        mistakes. Always sense-check AI output before acting on it. We accept no liability for
        business decisions taken solely on the basis of AI-generated text.
      </p>

      <h2>9. Intellectual property</h2>
      <p>
        The Services, their design, copy and underlying code — including the Strategic Flow™
        Method, Clarity Flow™, Strategy Flow™, Cash Confidence Flow™, CEO Decision Flow™ and
        Vision-Driven Growth Flow™ — remain the property of ProfitMIND Ltd. You retain ownership
        of the business data and content you enter — your forecast, your actuals, your goals are
        yours. You grant us a limited licence to process this data only to run the Services for you.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, ProfitMIND Ltd will not be liable for any indirect,
        consequential or special losses arising from your use of the Services, including but not
        limited to loss of profit, loss of revenue or loss of business opportunity. Our total
        liability to you, for any direct loss, in any twelve-month period, is limited to the amount
        you paid us for the Services in that period. Nothing in these terms excludes our liability
        for death or personal injury caused by our negligence, fraud, or any other liability that
        cannot lawfully be excluded.
      </p>

      <h2>11. Service availability</h2>
      <p>
        We aim for high availability but do not guarantee uninterrupted service. We may take the
        Services offline for maintenance, security or product changes with reasonable notice where
        practical. We are not liable for downtime caused by third-party providers (Cloudflare,
        TidyCal, Stripe, MailerLite, Resend, MongoDB Atlas, Anthropic, Emergent).
      </p>

      <h2>12. Account deletion (forecastly)</h2>
      <p>
        You can delete your forecastly account at any time from your account settings. This is
        irreversible: we will permanently wipe your forecast, actuals, goals and account profile,
        cancel any active subscription, and remove your email from our MailerLite list. Tax records
        (payments only) are retained for 6 years as required by HMRC.
      </p>

      <h2>13. Third-party links</h2>
      <p>
        Our Services contain links to third-party websites (such as TidyCal, LinkedIn, Instagram,
        forecastly, and our partner integrations). We have no control over the content of those
        sites and accept no responsibility for them or any loss you may suffer from using them.
      </p>

      <h2>14. Changes to these terms</h2>
      <p>
        We may update these terms occasionally. Material changes will be emailed to active
        customers at least 14 days in advance. The current version is always available at the URL
        where you first read these.
      </p>

      <h2>15. Governing law</h2>
      <p>
        These terms are governed by the laws of England and Wales. Any disputes will be subject to
        the exclusive jurisdiction of the courts of England and Wales.
      </p>

      <h2>16. Contact</h2>
      <p>
        For any question about these terms — including refunds, complaints or anything that didn't
        sit right — email <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a>. We aim to reply
        within 2 working days.
      </p>
    </LegalLayout>
  );
}
